import { useState } from 'react';
import useAssignVehicleToDevice from '../../../../API/hooks/vehicles/useAssignVehicleToDevice';
import useGetVehicles from '../../../../API/hooks/vehicles/useGetVehicles';
import { VehicleDetails as VehicleType } from '../../../../API/types/Vehicles';
import useVehicleThumbnail from '../../../../theme/useVehicleThumbnail';
import DropdownFilter from '../../../../components/common/DropdownFilter/DropdownFilter';
import { FilterConfig } from '../../../../components/common/DropdownFilter/types';
import Icon from '../../../../components/common/Icon/Icon';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import Tags from '../../../../components/common/Tags';
import { Button } from '../../../../components/ui/Button/Button';
import { queryFilters } from '../../../../utils/queryFilters';
import Vehicle from './Vehicle';
import { generateUnassignedVehiclesTitle } from '../helpers';
import S from './AssignVehicleToDeviceStyles';

type Props = {
  onCancel: () => void;
  deviceId: string;
  avatar: string;
  fleetId: string;
  model: string;
};

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { score_DESC: 'Score: most' },
      { score_ASC: 'Score: least' },
    ],
  },
] as FilterConfig[];

function AssignVehicleToDevice({
  model,
  onCancel,
  deviceId,
  fleetId,
  avatar,
}: Props): JSX.Element {
  const vehicleThumbnail = useVehicleThumbnail();
  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const { data, isLoading, isFetching } = useGetVehicles(fleetId, queryFilters(filters));
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleType | null>(null);
  const [assignError, setAssignError] = useState('');

  const onSubmitSuccess = (): void => {
    setSelectedVehicle(null);
    setAssignError('');
    onCancel();
  };
  const onSubmitFail = (error: string): void => {
    setAssignError(error);
  };

  const { mutate } = useAssignVehicleToDevice(onSubmitSuccess, onSubmitFail);

  const handleVehicleAssignment = (): void => mutate({ vehicleId: selectedVehicle!._id, fleetId, deviceId });

  const handleVehicleSelection = (vehicle: VehicleType): void => {
    setSelectedVehicle(() => vehicle);
  };

  return (
    <S.Wrapper>
      <div className="titles">
        <div className="action-bar">
          <div className="bar">
            <div className="assignment-container">
              <div className="device-overview">
                <Tags.ItemOverview
                  avatar={avatar}
                  avatarAlt="device"
                  title={model}
                  subtitle=""
                />
              </div>
              <Icon iconKey={IconKeys.ARROW_RIGHT} />
              <div className="vehicle-overview">
                {selectedVehicle
                  ? (
                    <Tags.Vehicle
                      imageSrc={vehicleThumbnail}
                      licensePlateNumber={selectedVehicle.licensePlateNumber}
                      model={`${selectedVehicle.manufacturer ? (`${selectedVehicle.manufacturer} `) : ''}${selectedVehicle.model ? (`${selectedVehicle.model} `) : ''}`}
                    />
                  )
                  : <div className="vehicle-placeholder"> Choose vehicle from below </div>}
              </div>
            </div>
            <div className="buttons-container">
              <Button
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                className="confirm-btn"
                disabled={!selectedVehicle}
                onClick={handleVehicleAssignment}
              >
                Assign vehicle
              </Button>
            </div>
          </div>
          {!!assignError && <div className="error"> {assignError} </div>}
        </div>
        <div className="vehicles-toolbar">
          <h3>{generateUnassignedVehiclesTitle(data?.results)}</h3>
          {
            SEARCH_CONFIG.map((filterConfig: FilterConfig) => (
              <DropdownFilter
                key={filterConfig.type}
                config={filterConfig}
                filters={filters}
                setFilters={setFilters}
              />
            ))
          }
        </div>
      </div>
      <div className="vehicles-list">
        {isLoading && <div> Loading... </div>}
        {!isLoading && data?.results && (
          <ul className="list-container">
              {!!data.results.length
                && data.results.map((item: VehicleType) => (
                  <Vehicle
                    vehicle={item}
                    isChecked={selectedVehicle?._id === item._id}
                    selectVehicle={handleVehicleSelection}
                  />
                ))}
              {!isFetching && !data.results.length && <div className="no-data">No vehicles found</div>}
          </ul>
        )}
      </div>
    </S.Wrapper>
  );
}

export default AssignVehicleToDevice;
